import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { post } from "../../lib/api";
import { PRICE_CURRENCIES, type PriceForm, readPriceForm } from "../../lib/priceInput";
import type { TariffVersion } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { MicroLabel } from "../ui/Text";
import { PriceFields } from "./PriceFields";

/**
 * Creating a price. pay-ui spec §6.1, §11.1.
 *
 * The fields, their conversion and their cross-field rules are `PriceFields` and
 * `lib/priceInput.ts`; this component is the verb. What is worth stating here is what
 * creating a price means as opposed to changing one: a new price starts with no
 * stations on it, so there is nothing to warn about and nothing to reprice. Every
 * caution in this file's counterpart, `ChangePrice`, is about the subscribers a price
 * already has.
 *
 * ## The four figures are one decision
 *
 * A price is not just a rate: the smallest and largest order bound what a driver can
 * pay in a single tap, and the one-tap buttons have to sit inside those bounds or they
 * are buttons that produce a refused order. So all of it is one form and the
 * cross-field rules are checked as the operator types, rather than arriving as a 400
 * after they have moved on.
 */

export interface CreatePriceProps {
  readonly onCreated: (price: TariffVersion) => void;
  readonly onCancel: (() => void) | undefined;
}

const EMPTY: PriceForm = {
  name: "",
  // First rather than blank: a currency is always one of two, and an empty select
  // is a field to be filled in rather than a decision to be made.
  currency: PRICE_CURRENCIES[0],
  price: "",
  minAmount: "",
  maxAmount: "",
  presets: "",
};

export function CreatePrice({ onCreated, onCancel }: CreatePriceProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PriceForm>(EMPTY);

  const draft = readPriceForm(form);
  const issues = draft.ok ? {} : draft.issues;

  const create = useMutation({
    mutationFn: (body: unknown) => post<TariffVersion>("/tariffs", body),
    onSuccess: (price) => {
      // The station sale panel's picker reads this list, and it is the reason
      // somebody is on this page at all.
      void queryClient.invalidateQueries({ queryKey: ["tariffs"] });
      onCreated(price);
    },
  });

  const canSubmit = draft.ok && !create.isPending;

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>New price</MicroLabel>
        {onCancel === undefined ? null : (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>

      {/* The server's own sentence, verbatim: it names the field it refused and
          that is the operator's next action. */}
      {create.isError ? <ErrorNotice error={create.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!draft.ok || create.isPending) return;
          create.mutate(draft.body);
        }}
        className="flex flex-col gap-5"
      >
        <PriceFields
          form={form}
          issues={issues}
          onChange={(field, value) => setForm((current) => ({ ...current, [field]: value }))}
          autoFocusName
        />

        <div className="flex items-end">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={create.isPending}>
            Create price
          </Button>
        </div>
      </form>

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        The figures can be changed later, and changing them mints a new version of this price rather
        than editing this one: every station on it follows, and every order already placed keeps the
        version it was quoted at. The currency cannot be changed afterwards. Put a station on this
        price from that station's page.
      </p>
    </div>
  );
}
